import { Box, Grid, Paper, Typography, LinearProgress } from "@mui/material";
import { addDays, format, parseISO } from "date-fns";
import { AvailabilityPreferences, AvailabilitySlot, WeeklyAvailabilityFromApi, PriorityData } from "../../utils/types";
import { formatDayAndDate, getSlotsForDay, getFirstSlotDate } from "../../utils/utils";
import { SessionsCreationFeedback } from "./SessionsCreationFeedback";
import { useStore } from "../../../../store/store";
import { createSessionsFromAvailabilitiesFinder } from "../../../../api/availabilities-finder";
import { Calendar, Views } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { localizer } from "../../../calendar/calendarUtils";
import { isLightColor } from "../../../../utils/isLightColor";
import { useState, useMemo } from "react";
import { SelectedSlotsList } from "./SelectedSlotList";
import { secondaryColor } from "../../../../theme";
import { getCalendarDataForDay } from "../../utils/utils";
import { CustomEvent } from "./CustomEvent";
import { handleSessionSoftValidation } from "../../../../screens/reserved-slots/validation/sessionSoftValidation";

type BypassParams = {
    bypass_reserved_slot_warning?: boolean;
    bypass_duplicate_service_warning?: boolean;
    bypass_no_schedule_warning?: boolean;
    bypassed_warning_reason?: string;
};

const minTime = new Date();
minTime.setHours(9, 0, 0);

const maxTime = new Date();
maxTime.setHours(21, 0, 0);

type AvailabilityResultsProps = {
    preferences: AvailabilityPreferences;
    weeks: WeeklyAvailabilityFromApi[];
    selectedSlots: AvailabilitySlot[];
    setSelectedSlots: (slots: AvailabilitySlot[]) => void;
    setWeeks: (weeks: WeeklyAvailabilityFromApi[]) => void;
    isLoading: boolean;
    setIsLoading: (isLoading: boolean) => void;
    prioritiesData?: PriorityData;
};

export const AvailabilityResults = ({
    preferences,
    weeks,
    selectedSlots,
    setSelectedSlots,
    setWeeks,
    isLoading,
    setIsLoading,
    prioritiesData,
}: AvailabilityResultsProps) => {
    const { notifications } = useStore();

    // Store custom titles for slots
    const [slotTitles, setSlotTitles] = useState<{ [slotId: string]: string }>({});

    const addSlotToSelectedSlots = (slot: AvailabilitySlot) => {
        setSelectedSlots([...selectedSlots, slot]);
    };

    const removeSlotFromSelectedSlots = (slot: AvailabilitySlot | AvailabilitySlot[]) => {
        if (Array.isArray(slot)) {
            setSelectedSlots(selectedSlots.filter((s) => !slot.includes(s)));
        } else {
            setSelectedSlots(selectedSlots.filter((s) => s.slotId !== slot.slotId));
        }
        // Remove title if slot is removed
        const slotIdsToRemove = Array.isArray(slot) ? slot.map((s) => s.slotId) : [slot.slotId];
        setSlotTitles((prev) => {
            const updated = { ...prev };
            slotIdsToRemove.forEach((id) => delete updated[id]);
            return updated;
        });
    };

    const handleSlotTitleChange = (slotId: string, value: string) => {
        const updatedTitles = { ...slotTitles, [slotId]: value };
        setSlotTitles(updatedTitles);
    };

    const handleCreateSessions = async (
        selectedSlots: Array<AvailabilitySlot>,
        preferences: AvailabilityPreferences,
        bypassParams?: BypassParams
    ) => {
        if (selectedSlots.length === 0) {
            return notifications.setNotification({
                message: "Tem que selecionar pelo menos uma sessão",
                isOpen: true,
                severity: "error",
            });
        }

        setIsLoading(true);

        // Attach custom titles and bypass info to slots
        const preparedSlots = selectedSlots.map((slot) => ({
            ...slot,
            title: slotTitles[slot.slotId] || "",
            ...bypassParams,
        }));

        try {
            const data = await createSessionsFromAvailabilitiesFinder(
                preferences,
                preparedSlots
            );

        const autoHideDuration = 20000;

        if (!data.isValid) {
            notifications.setNotification({
                message: (
                    <SessionsCreationFeedback
                        sessionsOrSlots={data.slotsNotAvailable}
                        message={data.message || "Erro ao criar sessões"}
                    />
                ),
                isOpen: true,
                severity: "error",
                autoHideDuration,
            });
                return;
            }

            notifications.setNotification({
                message: (
                    <SessionsCreationFeedback sessionsOrSlots={data.sessionsCreated} message="Sessões criadas com sucesso" />
                ),
                isOpen: true,
                severity: "success",
                autoHideDuration,
            });

        setSelectedSlots([]);
        setWeeks([]);
        setSlotTitles({});
        } catch (error: any) {
            const wasHandled = await handleSessionSoftValidation(
                error,
                "sessions",
                (bypassParams) => handleCreateSessions(selectedSlots, preferences, bypassParams)
            );

            if (!wasHandled) {
                const errorMessage = error?.response?.data?.errors?.[0]?.message ??error?.response?.data?.message ?? "Erro ao criar sessões";
                notifications.setNotification({
                    isOpen: true,
                    message: errorMessage,
                    severity: "error",
                });
            }
        } finally {
        setIsLoading(false);
        }
    };

    // Empty component to hide the date header
    const EmptyHeader = () => null;

    const calendarFormats = {
        timeGutterFormat: "HH:mm",
        eventTimeRangeFormat: (args: { start: Date; end: Date }, _culture?: string, _local?: any) =>
            `${format(args.start, "HH:mm")} - ${format(args.end, "HH:mm")}`,
    };

    // Memoize weeks rendering to avoid re-rendering calendars on slotTitles change
    const weeksContent = useMemo(() => {
        return (
            <Box sx={{ p: 3, backgroundColor: "background.default" }}>
                {/* Render each week with its own grid of days */}
                {weeks.map((week, weekIndex) => {
                    const weekStartDate = parseISO(week.weekStartDate);
                    // Filter out 'sunday' and group days into rows of 3 columns for this week
                    const days = preferences.preferredDays.filter((day) => day !== "sunday");
                    const dayChunks = [];
                    for (let i = 0; i < days.length; i += 3) {
                        dayChunks.push(days.slice(i, i + 3));
                    }
                    // const serviceNamesOrdered = preferences.services.map((s) => s.name);
                    return (
                        <Box key={weekIndex} sx={{ pt: 4, pb: 6, textAlign: "center" }}>
                            <Typography variant="h5" gutterBottom color="primary" sx={{ mb: 2 }}>
                                Semana de {format(addDays(weekStartDate, 1), "PP")}
                            </Typography>

                            {dayChunks.map((dayChunk, rowIndex) => (
                                <Grid container spacing={4} key={rowIndex} sx={{ mb: 3 }}>
                                    {dayChunk.map((day) => {
                                        const dayIndex = preferences.preferredDays.indexOf(day);
                                        const slots = week.slots[day] || [];
                                        const firstSlotDate = getFirstSlotDate(slots, weekStartDate, dayIndex);
                                        const slotsOfTheDay = getSlotsForDay(weekStartDate, day, slots, selectedSlots);
                                        const { resources, events } = getCalendarDataForDay(
                                            slotsOfTheDay,
                                            selectedSlots,
                                            preferences.firstService,
                                            preferences.secondService
                                        );

                                        return (
                                            <Grid item xs={12} md={4} key={day}>
                                                <Paper elevation={3} sx={{ p: 2, borderRadius: 3, height: "100%" }}>
                                                    <Typography variant="h6" sx={{ mb: 2 }}>
                                                        {formatDayAndDate(day, weekStartDate)}
                                                    </Typography>

                                                    <Box sx={{ mt: 1, height: 600 }}>
                                                        <Calendar
                                                            localizer={localizer}
                                                            events={events}
                                                            resources={resources}
                                                            resourceIdAccessor="resourceId"
                                                            resourceTitleAccessor="resourceTitle"
                                                            defaultView={Views.DAY}
                                                            views={{ day: true }}
                                                            min={minTime}
                                                            max={maxTime}
                                                            step={15}
                                                            timeslots={4}
                                                            toolbar={false}
                                                            defaultDate={firstSlotDate}
                                                            selectable
                                                            onSelectEvent={(event) => {
                                                                if (event.isSelected) {
                                                                    removeSlotFromSelectedSlots(event.slotData);
                                                                } else {
                                                                    addSlotToSelectedSlots(event.slotData);
                                                                }
                                                            }}
                                                            eventPropGetter={(event) => {
                                                                const defaultColor = "#d1cfcf";
                                                                // @ts-ignore
                                                                const eventColor = event?.color ? event?.color : defaultColor;

                                                                const style: React.CSSProperties = {
                                                                    backgroundColor: eventColor,
                                                                    borderColor: "white",
                                                                    color: isLightColor(eventColor) ? "#404040" : "#fff",
                                                                    fontSize: "11px",
                                                                };
                                                                if (event.isSelected) {
                                                                    style.boxShadow = `0 0 0 1px ${secondaryColor}, 0 0 4px 1px ${secondaryColor}`;
                                                                    style.borderColor = `${secondaryColor}`;
                                                                }
                                                                return { style };
                                                            }}
                                                            components={{
                                                                toolbar: () => null,
                                                                day: {
                                                                    header: EmptyHeader,
                                                                },
                                                                event: (props) => {
                                                                    return (
                                                                        <CustomEvent
                                                                            props={props}
                                                                            prioritiesData={prioritiesData}
                                                                        />
                                                                    );
                                                                },
                                                            }}
                                                            formats={calendarFormats}
                                                        />
                                                    </Box>
                                                </Paper>
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            ))}
                        </Box>
                    );
                })}
            </Box>
        );
    }, [weeks, preferences, selectedSlots]);

    if (isLoading) {
        return <LinearProgress color="secondary" />;
    }

    return (
        <Box sx={{ position: "relative" }}>
            {weeksContent}
            <SelectedSlotsList
                selectedSlots={selectedSlots}
                slotTitles={slotTitles}
                onTitleChange={handleSlotTitleChange}
                onRemoveSlot={removeSlotFromSelectedSlots}
                onCreateSessions={() => handleCreateSessions(selectedSlots, preferences)}
            />
        </Box>
    );
};
